import React, { Suspense, lazy, useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons/faPaperPlane';
import '../../styles/botWidget.css';

const ReactMarkdown = lazy(() => import('react-markdown'));

const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true">
    <path d="M12 0C13 8 16 11 24 12 16 13 13 16 12 24 11 16 8 13 0 12 8 11 11 8 12 0Z" />
  </svg>
);

const detectLang = () => ((navigator.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en');

const GREETING = {
  es: 'Hola, soy el asistente del portfolio. Pregúntame sobre proyectos, skills o experiencia.',
  en: "Hi, I'm the portfolio assistant. Ask me about projects, skills or experience."
};

const ERROR_TEXT = {
  es: 'No pude responder ahora mismo. Intenta de nuevo en un momento.',
  en: 'Could not respond right now. Try again in a moment.'
};

const PLACEHOLDER = {
  es: 'Escribí tu pregunta...',
  en: 'Type your question...'
};

const BotWidget = () => {
  const lang = useRef(detectLang()).current;
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => [{ role: 'bot', content: GREETING[lang] }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const toggleOpen = useCallback(() => setIsOpen((prev) => !prev), []);

  const sendMessage = useCallback(
    async (event) => {
      event.preventDefault();
      const question = input.trim();
      if (!question || isLoading) return;

      const history = messages
        .slice(-3)
        .map((message) => ({ role: message.role === 'bot' ? 'assistant' : 'user', content: message.content }));

      setMessages((prev) => [...prev, { role: 'user', content: question }]);
      setInput('');
      setIsLoading(true);

      try {
        const response = await fetch('/.netlify/functions/bot-proxy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question, lang, history })
        });
        const data = await response.json();
        const answer = response.ok && data.answer ? data.answer : ERROR_TEXT[lang];
        setMessages((prev) => [...prev, { role: 'bot', content: answer }]);
      } catch (error) {
        setMessages((prev) => [...prev, { role: 'bot', content: ERROR_TEXT[lang] }]);
      } finally {
        setIsLoading(false);
      }
    },
    [input, isLoading, lang, messages]
  );

  return (
    <div className="bot-widget">
      {isOpen ? (
        <div className="bot-widget__panel" role="dialog" aria-label="Portfolio chat">
          <div className="bot-widget__header">
            <span>Portfolio Assistant</span>
            <button
              type="button"
              className="bot-widget__close"
              onClick={toggleOpen}
              aria-label={lang === 'es' ? 'Cerrar chat' : 'Close chat'}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className="bot-widget__messages">
            {messages.map((message, index) => (
              <div key={index} className={`bot-widget__bubble bot-widget__bubble--${message.role}`}>
                {message.role === 'bot' ? (
                  <Suspense fallback={message.content}>
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </Suspense>
                ) : (
                  message.content
                )}
              </div>
            ))}
            {isLoading ? (
              <div className="bot-widget__bubble bot-widget__bubble--bot bot-widget__bubble--typing" aria-live="polite">
                <span />
                <span />
                <span />
              </div>
            ) : null}
          </div>

          <form className="bot-widget__form" onSubmit={sendMessage}>
            <input
              type="text"
              className="bot-widget__input"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={PLACEHOLDER[lang]}
              aria-label={lang === 'es' ? 'Mensaje' : 'Message'}
            />
            <button
              type="submit"
              className="bot-widget__send"
              disabled={isLoading || !input.trim()}
              aria-label={lang === 'es' ? 'Enviar' : 'Send'}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </form>
        </div>
      ) : null}

      <button
        type="button"
        className="bot-widget__fab"
        onClick={toggleOpen}
        aria-label={isOpen ? (lang === 'es' ? 'Cerrar chat' : 'Close chat') : (lang === 'es' ? 'Abrir chat del portfolio' : 'Open portfolio chat')}
      >
        {isOpen ? <FontAwesomeIcon icon={faXmark} /> : <SparkleIcon />}
      </button>
    </div>
  );
};

export default BotWidget;
